/** Sentinel for select fields meaning "no filter". */
export const ADVANCED_FILTER_ALL = "__all__";

/**
 * @param {*} value
 * @param {{ allSentinel?: string }} [options]
 */
function isFilledFilterBound(value) {
  if (value == null) return false;
  return String(value).trim() !== "";
}

export function isActiveAdvancedFilterValue(value, options = {}) {
  const all = options.allSentinel ?? ADVANCED_FILTER_ALL;
  if (value == null) return false;
  if (value === all) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return false;
    // Legacy / mistaken "All" sentinel (e.g. copied from toolbar status) must not filter rows.
    if (trimmed.toLowerCase() === "all") return false;
  }
  if (typeof value === "object") {
    return (
      isFilledFilterBound(value.min) ||
      isFilledFilterBound(value.max) ||
      isFilledFilterBound(value.from) ||
      isFilledFilterBound(value.to)
    );
  }
  return true;
}

/**
 * @param {Record<string, *>} criteria
 * @param {{ allSentinel?: string }} [options]
 */
export function countActiveAdvancedCriteria(criteria, options = {}) {
  if (!criteria || typeof criteria !== "object") return 0;
  return Object.values(criteria).filter((v) => isActiveAdvancedFilterValue(v, options)).length;
}

function formatRangeBound(value, { percent = false } = {}) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return percent ? `${text}%` : text;
}

/**
 * Human-readable summary for one active advanced-filter criterion.
 *
 * @param {{ id?: string, label?: string, type?: string, options?: Array<{ value: *, label?: string }> }} field
 * @param {*} value
 * @returns {string}
 */
function formatAdvancedFilterCriterionSummary(field, value) {
  if (!isActiveAdvancedFilterValue(value)) return "";
  const label = String(field?.label || field?.id || "Filter").trim() || "Filter";
  const type = field?.type || "text";

  if (type === "select") {
    const raw = String(value);
    const options = Array.isArray(field?.options) ? field.options : [];
    const match = options.find((opt) => String(opt?.value) === raw);
    const display = match?.label != null && String(match.label).trim() !== "" ? String(match.label) : raw;
    return `${label}: ${display}`;
  }

  if (type === "number" || type === "percent" || type === "date") {
    if (!value || typeof value !== "object") return "";
    const percent = type === "percent";
    if (type === "date") {
      const from = String(value.from ?? "").trim();
      const to = String(value.to ?? "").trim();
      if (from && to) return `${label}: ${from} – ${to}`;
      if (from) return `${label}: from ${from}`;
      if (to) return `${label}: to ${to}`;
      return "";
    }
    const min = formatRangeBound(value.min, { percent });
    const max = formatRangeBound(value.max, { percent });
    if (min && max) return `${label}: ${min} – ${max}`;
    if (min) return `${label}: ≥ ${min}`;
    if (max) return `${label}: ≤ ${max}`;
    return "";
  }

  const text = String(value ?? "").trim();
  if (!text) return "";
  return `${label}: contains ${text}`;
}

/**
 * Active advanced-filter criteria as tooltip clause strings (field order).
 *
 * @param {Record<string, *>} criteria
 * @param {Array<{ id: string, label?: string, type?: string, options?: Array<{ value: *, label?: string }> }>} fields
 * @param {{ allSentinel?: string }} [options]
 * @returns {string[]}
 */
export function listAdvancedFilterActiveSummaryLines(criteria, fields, options = {}) {
  if (!criteria || typeof criteria !== "object") return [];
  const fieldList = Array.isArray(fields) ? fields.filter((f) => f?.id) : [];
  const byId = new Map(fieldList.map((f) => [f.id, f]));
  const lines = [];

  // Prefer field definition order so the tooltip matches the slide-over.
  fieldList.forEach((field) => {
    if (!isActiveAdvancedFilterValue(criteria[field.id], options)) return;
    const line = formatAdvancedFilterCriterionSummary(field, criteria[field.id]);
    if (line) lines.push(line);
  });

  // Include any active keys not present in the current field list (stale after column change).
  Object.keys(criteria).forEach((id) => {
    if (byId.has(id)) return;
    if (!isActiveAdvancedFilterValue(criteria[id], options)) return;
    const line = formatAdvancedFilterCriterionSummary({ id, label: id, type: "text" }, criteria[id]);
    if (line) lines.push(line);
  });

  return lines;
}

/**
 * One-line tooltip text for active advanced-filter criteria (native `title`).
 * Clauses are joined with AND to match slide-over "match every field" semantics.
 *
 * @param {Record<string, *>} criteria
 * @param {Array<{ id: string, label?: string, type?: string, options?: Array<{ value: *, label?: string }> }>} fields
 * @param {{ allSentinel?: string }} [options]
 * @returns {string}
 */
export function formatAdvancedFilterActiveSummary(criteria, fields, options = {}) {
  return listAdvancedFilterActiveSummaryLines(criteria, fields, options).join(" AND ");
}

/**
 * Map stored criteria onto form control values (blank text, "All" selects, empty ranges).
 *
 * @param {Record<string, *>} applied
 * @param {Array<{ id: string, type?: string, options?: Array<{ value: * }> }>} fields
 * @param {{ allSentinel?: string }} [options]
 */
export function advancedFilterAppliedToFormValues(applied, fields, options = {}) {
  const all = options.allSentinel ?? ADVANCED_FILTER_ALL;
  const raw = applied && typeof applied === "object" ? applied : {};
  const next = {};
  (Array.isArray(fields) ? fields : []).forEach((f) => {
    if (!f?.id) return;
    if (f.type === "select") {
      const optionList = Array.isArray(f.options) ? f.options.filter(Boolean) : [];
      const allowed = new Set([all, ...optionList.map((opt) => String(opt.value))]);
      const rawStr = raw[f.id] != null ? String(raw[f.id]).trim() : "";
      next[f.id] = rawStr !== "" && allowed.has(rawStr) ? rawStr : all;
      return;
    }
    if (f.type === "number" || f.type === "percent") {
      const value = raw[f.id];
      next[f.id] = {
        min: value && typeof value === "object" ? String(value.min ?? "") : "",
        max: value && typeof value === "object" ? String(value.max ?? "") : "",
      };
      return;
    }
    if (f.type === "date") {
      const value = raw[f.id];
      next[f.id] = {
        from: value && typeof value === "object" ? String(value.from ?? "") : "",
        to: value && typeof value === "object" ? String(value.to ?? "") : "",
      };
      return;
    }
    next[f.id] = raw[f.id] != null ? String(raw[f.id]) : "";
  });
  return next;
}

function cleanedRangeCriteria(value) {
  if (!value || typeof value !== "object") return null;
  const cleaned = {};
  ["min", "max", "from", "to"].forEach((key) => {
    if (isFilledFilterBound(value[key])) cleaned[key] = String(value[key]).trim();
  });
  return isActiveAdvancedFilterValue(cleaned) ? cleaned : null;
}

/**
 * Live filter update: store only active criteria for one field.
 *
 * @param {Record<string, *>} prev
 * @param {string} fieldId
 * @param {*} value
 * @param {{ allSentinel?: string }} [options]
 */
export function patchAdvancedFilterCriteria(prev, fieldId, value, options = {}) {
  const next = { ...(prev && typeof prev === "object" ? prev : {}) };
  if (!fieldId) return next;
  if (value && typeof value === "object") {
    const cleaned = cleanedRangeCriteria(value);
    if (cleaned) next[fieldId] = cleaned;
    else delete next[fieldId];
    return next;
  }
  if (!isActiveAdvancedFilterValue(value, options)) {
    delete next[fieldId];
    return next;
  }
  next[fieldId] = typeof value === "string" ? value.trim() : value;
  return next;
}

/**
 * Client-side filter: keep rows where every active criterion matches (AND).
 *
 * @template T
 * @param {T[]} rows
 * @param {Record<string, *>} criteria
 * @param {{ matchers: Record<string, (row: T, value: *, ctx: *) => boolean>, ctx?: *, combineMode?: 'and' | 'or', allSentinel?: string }} config
 */
export function applyAdvancedFilterRows(rows, criteria, config = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const matchers = config.matchers || {};
  const ctx = config.ctx;
  const allSentinel = config.allSentinel ?? ADVANCED_FILTER_ALL;
  const combineMode = config.combineMode === "or" ? "or" : "and";

  const active = Object.entries(criteria || {}).filter(([, v]) =>
    isActiveAdvancedFilterValue(v, { allSentinel })
  );
  if (active.length === 0) return list;

  return list.filter((row) => {
    const checks = active.map(([id, value]) => {
      const fn = matchers[id];
      if (typeof fn !== "function") return true;
      return fn(row, value, ctx);
    });
    return combineMode === "or" ? checks.some(Boolean) : checks.every(Boolean);
  });
}
